import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface PaloSortDefault {
    /** The column the arrow stands on. */
    key: string;
    order: 'asc' | 'desc';
    /**
     * What the server is asked for, where the order is more than one column.
     *
     * The invoices open on their own order — drafts without a number first,
     * then by invoice date — which no single column names. The arrow belongs
     * on the column that decides the order the reader sees, the request names
     * the order itself.
     */
    requestKey?: string;
}

/**
 * The order a list opens in, for the list and its cockpit tab alike.
 *
 * "Jede Belegliste öffnet mit dem jüngsten Beleg ganz oben" (LST-S16, GF
 * 12.10.2026). Kept here rather than at each list, because the cockpit tab and
 * the list it belongs to were free to differ before — the cockpit asked for no
 * order at all and got the oldest document on top, while its list asked for
 * the newest (#2234).
 *
 * Master data is left out on purpose: those lists are looked things up in, not
 * worked through in time, and stay sorted by number.
 */
export const PALO_SORT_DEFAULTS: Record<string, PaloSortDefault> = {
    inquiries: { key: 'number', order: 'desc' },
    'purchase-orders': { key: 'number', order: 'desc' },
    invoices: { key: 'invoiceDate', order: 'desc', requestKey: 'standard' },
    'sales-orders': { key: 'createdAt', order: 'desc' },
    demands: { key: 'createdAt', order: 'desc' },
    'bonus-settlements': { key: 'year', order: 'desc' },
    /** The member's articles to buy again: the ones ordered most first (#2280). */
    'buy-again': { key: 'frequent', order: 'desc' },
};

/** The order a list opens in; newest first for anything not named above. */
export const paloSortDefault = (listKey: string): PaloSortDefault =>
    PALO_SORT_DEFAULTS[listKey] ?? { key: 'createdAt', order: 'desc' };

/**
 * The parameters a request carries, with the list's own order filled in where
 * the address names none.
 *
 * The cockpit asks one endpoint for all five document types, from a component
 * above the tabs — so the order cannot come from the tab itself and is put in
 * here, by the tab the address points at.
 */
export const paloSortedParams = (searchParams: URLSearchParams, listKey: string): URLSearchParams => {
    if (searchParams.get('sort')) {
        return searchParams;
    }

    const fallback = paloSortDefault(listKey);
    const next = new URLSearchParams(searchParams.toString());

    next.set('sort', fallback.requestKey ?? fallback.key);
    next.set('order', fallback.order);

    return next;
};

/**
 * The sorting a list shows, and the parameters it asks the server with.
 *
 * A sorting the user chose stands in the address and wins; until then the list
 * opens in its own order. The request always carries one — a list that asks
 * for none is answered in the order the engine happens to hold, which is how
 * the cockpit came to open on a document from 2019.
 */
export const usePaloListSort = (
    listKey: string
): { sort: { key: string; order: 'asc' | 'desc' }; params: URLSearchParams } => {
    const [searchParams] = useSearchParams();
    const search = searchParams.toString();
    const fallback = paloSortDefault(listKey);
    const chosen = searchParams.get('sort');
    const key = chosen ?? fallback.key;
    const order = (searchParams.get('order') as 'asc' | 'desc' | null) ?? fallback.order;

    // The lists fetch on this object, so it may only change when the sorting
    // does — a fresh one on every render would fetch on every render.
    const params = useMemo(() => {
        const next = new URLSearchParams(search);

        next.set('sort', chosen ?? fallback.requestKey ?? fallback.key);
        next.set('order', order);

        return next;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, chosen, order, listKey]);

    return { sort: { key, order }, params };
};
